import React, { useState } from 'react';
import { useAuth } from '../../auth/AuthContext';
import { useSleepSessions } from '../../hooks/useWearables';
import {
  Card,
  Container,
  Grid,
  Group,
  Stack,
  Text,
  Title,
  Button,
  Badge,
  Skeleton,
  Select,
  TextInput,
  Alert,
} from '@mantine/core';
import { IconRefresh, IconClock } from '@tabler/icons-react';

function formatDuration(seconds) {
  if (!seconds) return '-';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

const RANGE_OPTIONS = [
  { value: 'all', label: 'All time' },
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: 'custom', label: 'Custom range' },
];

function getDateRange(days) {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - (days === 'all' ? 12000 : Number(days)));
  return { start_date: start.toISOString().split('T')[0], end_date: end.toISOString().split('T')[0] };
}

function Sleep() {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [rangeDays, setRangeDays] = useState('all');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [cursor, setCursor] = useState(null);

  const selectedRange = rangeDays === 'custom'
    ? (
      customStart
        ? { start_date: customStart, end_date: customEnd || new Date().toISOString().split('T')[0] }
        : getDateRange(7)
    )
    : getDateRange(rangeDays);

  const { sessions, pagination, loading, error, refresh } = useSleepSessions({
    limit: 50,
    startDate: selectedRange.start_date,
    endDate: selectedRange.end_date,
    cursor,
  });

  const avgDuration = sessions.length > 0
    ? sessions.reduce((s, x) => s + (x.duration_seconds || x.total_sleep_time_seconds || 0), 0) / sessions.length
    : 0;
  const withEff = sessions.filter((x) => x.efficiency_percent != null);
  const avgEfficiency = withEff.length > 0 ? withEff.reduce((s, x) => s + (x.efficiency_percent || 0), 0) / withEff.length : null;

  const handleRangeChange = (value) => {
    const nextRange = value || '7';
    setCursor(null);
    setRangeDays(nextRange);
    if (nextRange !== 'custom') {
      refresh({ ...getDateRange(nextRange), limit: 50, cursor: null });
    }
  };

  if (authLoading) return (<Container size="xl" py={{ base: 'lg', sm: 'xl' }} maw={1400}><Skeleton height={400} /></Container>);
  if (!isAuthenticated) return (<Container size="xl" py={{ base: 'lg', sm: 'xl' }} maw={1400}><Card withBorder><Title order={2}>Sleep</Title><Text c="dimmed" mt="xs">Please log in.</Text></Card></Container>);

  return (
    <Container size="xl" py={{ base: 'lg', sm: 'xl' }} maw={1400} style={{ marginTop: 0 }}>
      <Stack gap="md">
        <Title order={1} size="h2" style={{ marginTop: 0 }}>Sleep</Title>
        {error && <Alert color="red" variant="light">{error}</Alert>}
        <Card withBorder radius="md" p="md">
          <Group justify="space-between" mb="md">
            <Text fw={600}>Sleep Sessions</Text>
            <Group>
              <Select data={RANGE_OPTIONS} value={rangeDays} onChange={handleRangeChange} w={140} />
              <Button
                variant="light"
                leftSection={<IconRefresh size={16} />}
                onClick={() => {
                  setCursor(null);
                  refresh({ ...selectedRange, limit: 50, cursor: null });
                }}
                disabled={loading}
              >
                Refresh
              </Button>
            </Group>
          </Group>
          {rangeDays === 'custom' && (
            <Group mb="md">
              <TextInput
                label="Start date"
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
              />
              <TextInput
                label="End date"
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
              />
              <Button
                mt={24}
                variant="light"
                size="sm"
                onClick={() => {
                  setCursor(null);
                  refresh({ ...selectedRange, limit: 50, cursor: null });
                }}
                disabled={!customStart || loading}
              >
                Load Data
              </Button>
            </Group>
          )}
          <Grid mb="md">
            <Grid.Col span={4}><Text size="sm" c="dimmed">Sessions</Text><Text size="lg" fw={700}>{sessions.length}</Text></Grid.Col>
            <Grid.Col span={4}><Text size="sm" c="dimmed">Avg Duration</Text><Text size="lg" fw={700}>{formatDuration(avgDuration)}</Text></Grid.Col>
            <Grid.Col span={4}><Text size="sm" c="dimmed">Avg Efficiency</Text><Text size="lg" fw={700}>{avgEfficiency != null ? `${Math.round(avgEfficiency)}%` : '-'}</Text></Grid.Col>
          </Grid>
          {loading ? (
            <Stack gap="xs">{[1,2,3,4,5].map((i) => <Skeleton key={i} height={100} />)}</Stack>
          ) : sessions.length > 0 ? (
            <Stack gap="md">
              {sessions.map((session, idx) => {
                const startDate = new Date(session.start_datetime || session.start_time || session.date);
                const duration = formatDuration(session.duration_seconds || session.total_sleep_time_seconds);
                return (
                  <Card key={idx} withBorder radius="md" p="md">
                    <Group justify="space-between" mb="xs">
                      <div>
                        <Text fw={600}>{session.is_nap ? 'Nap' : 'Sleep'}</Text>
                        <Text size="xs" c="dimmed">{startDate.toLocaleDateString()} • {startDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</Text>
                      </div>
                      {session.efficiency_percent != null && <Badge color="indigo" variant="light">{Math.round(session.efficiency_percent)}% efficiency</Badge>}
                    </Group>
                    <Group gap="md">
                      <Group gap="xs"><IconClock size={16} /><Text size="sm">{duration}</Text></Group>
                      {session.recovery_score != null && <Badge color={session.recovery_score >= 67 ? 'green' : session.recovery_score >= 34 ? 'yellow' : 'red'}>Recovery: {session.recovery_score}%</Badge>}
                    </Group>
                  </Card>
                );
              })}
            </Stack>
          ) : (
            <Text c="dimmed" ta="center" py="xl">No sleep data available</Text>
          )}
          {(pagination?.previous_cursor || pagination?.has_more) && (
            <Group justify="space-between" mt="md">
              <Button
                variant="light"
                disabled={!pagination?.previous_cursor || loading}
                onClick={() => {
                  setCursor(pagination.previous_cursor);
                  refresh({ ...selectedRange, limit: 50, cursor: pagination.previous_cursor });
                }}
              >
                Previous Page
              </Button>
              <Text size="sm" c="dimmed">
                {pagination?.total_count != null ? `${pagination.total_count} total` : 'Paginated results'}
              </Text>
              <Button
                variant="light"
                disabled={!pagination?.has_more || loading}
                onClick={() => {
                  setCursor(pagination.next_cursor);
                  refresh({ ...selectedRange, limit: 50, cursor: pagination.next_cursor });
                }}
              >
                Next Page
              </Button>
            </Group>
          )}
        </Card>
      </Stack>
    </Container>
  );
}

export default Sleep;
